import Logo from '@/components/logo';
import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/input-password';
import { toast } from '@/hooks/use-toast';
import { registerMutationFn } from '@/lib/api';
import z, { useZodForm } from '@/lib/zod';
import { SignUpSchema } from '@/schemas';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, Loader, MailCheckIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function SignUp() {
    const [isSubmitted, setIsSubmitted] = useState(false);

    const { mutate, isPending } = useMutation({
        mutationFn: registerMutationFn,
    });

    const form = useZodForm(SignUpSchema, {
        defaultValues: {
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
        },
        mode: 'onChange',
    });

    const onSubmit = (values: z.infer<typeof SignUpSchema>) => {
        mutate(values, {
            onSuccess: (response) => {
                setIsSubmitted(true);
                toast({
                    title: 'Done',
                    description: response.data.message,
                    className: 'text-white',
                    variant: 'success',
                });
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    description: error.message,
                    variant: 'destructive',
                });
            },
        });
    };

    const password = form.watch('password');
    const confirmPassword = form.watch('confirmPassword');

    useEffect(() => {
        if (password || confirmPassword) {
            form.trigger(['password', 'confirmPassword']);
        }
    }, [password, confirmPassword, form]);

    return (
        <>
            <main className='w-full min-h-[590px] h-auto max-w-full pt-10'>
                {!isSubmitted ? (
                    <div className='w-full p-5 rounded-md'>
                        <Logo size='100px' />

                        <h1 className='text-xl tracking-[-0.16px] dark:text-[#fcfdffef] font-bold mb-1.5 mt-8 text-center'>
                            Create a new account
                        </h1>
                        <p className='mb-5 text-center text-base dark:text-[#f1f7feb5] font-normal'>
                            Already have an account?{' '}
                            <Link
                                className='text-primary'
                                to='/login'>
                                Sign in
                            </Link>
                            .
                        </p>
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)}>
                                <div className='mb-4'>
                                    <FormField
                                        control={form.control}
                                        name='name'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className='dark:text-[#f1f7feb5] text-sm'>
                                                    Name
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        placeholder='Your Full Name'
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className='mb-4'>
                                    <FormField
                                        control={form.control}
                                        name='email'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className='dark:text-[#f1f7feb5] text-sm'>
                                                    Email
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        placeholder='user@example.com'
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className='mb-4'>
                                    <FormField
                                        control={form.control}
                                        name='password'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className='dark:text-[#f1f7feb5] text-sm'>
                                                    Password
                                                </FormLabel>
                                                <FormControl>
                                                    <PasswordInput
                                                        {...field}
                                                        placeholder='••••••••••••'
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className='mb-4'>
                                    <FormField
                                        control={form.control}
                                        name='confirmPassword'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className='dark:text-[#f1f7feb5] text-sm'>
                                                    Confirm Password
                                                </FormLabel>
                                                <FormControl>
                                                    <PasswordInput
                                                        {...field}
                                                        placeholder='••••••••••••'
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <Button
                                    disabled={isPending}
                                    className='w-full text-[15px] h-[40px] !bg-blue-500 text-white font-semibold'
                                    type='submit'>
                                    {isPending && (
                                        <Loader className='animate-spin' />
                                    )}
                                    Create account
                                    <ArrowRight />
                                </Button>

                                <div className='mb-3 mt-4 flex items-center justify-center'>
                                    <div
                                        aria-hidden='true'
                                        className='h-px w-full bg-[#eee] dark:bg-[#d6ebfd30]'
                                        data-orientation='horizontal'
                                        role='separator'></div>
                                    <span className='mx-4 text-xs dark:text-[#f1f7feb5] font-normal'>
                                        OR
                                    </span>
                                    <div
                                        aria-hidden='true'
                                        className='h-px w-full bg-[#eee] dark:bg-[#d6ebfd30]'
                                        data-orientation='horizontal'
                                        role='separator'></div>
                                </div>
                            </form>
                        </Form>
                        <Button
                            variant='outline'
                            className='w-full h-[40px]'>
                            Email magic link
                        </Button>
                        <p className='text-xs font-normal mt-4'>
                            By signing up, you agree to our{' '}
                            <a
                                className='text-primary hover:underline'
                                href='#'>
                                Terms of Service
                            </a>{' '}
                            and{' '}
                            <a
                                className='text-primary hover:underline'
                                href='#'>
                                Privacy Policy
                            </a>
                            .
                        </p>
                    </div>
                ) : (
                    <div className='w-full h-[80vh] flex flex-col gap-2 items-center justify-center rounded-md'>
                        <div className='size-[48px]'>
                            <MailCheckIcon
                                size='48px'
                                className='animate-bounce'
                            />
                        </div>
                        <h2 className='text-center text-xl tracking-[-0.16px] dark:text-[#fcfdffef] font-bold'>
                            You have successfully registered
                            <br />
                            Check your email
                        </h2>
                        <p className='mb-2 text-center text-sm text-muted-foreground dark:text-[#f1f7feb5] font-normal'>
                            We just sent a verification link to{' '}
                            {form.getValues().email}.
                        </p>
                        <Link to='/login'>
                            <Button className='h-[40px]'>
                                Go to login
                                <ArrowRight />
                            </Button>
                        </Link>
                    </div>
                )}
            </main>
        </>
    );
}
